/**
 * Microsoft Entra ID (MSAL) authentication provider.
 *
 * Two things here are deliberate and easy to undo by accident.
 *
 * **The auth state is derived, not stored.** `isAuthenticated`, `user` and
 * `loading` are pure functions of `authEnabled`, MSAL's `accounts` and its
 * `inProgress`, so they are computed with `useMemo` rather than written into
 * state from an effect. Setting state in an effect costs an extra render pass
 * per change and is what `react-hooks/set-state-in-effect` warns about.
 *
 * **`loading` keys off `accounts`, not only `inProgress`.** MSAL sets
 * `inProgress` to `"acquireToken"` during a silent token refresh, which happens
 * every twelve minutes. Gating purely on `inProgress === "none"` would flip a
 * signed-in user to "loading / not authenticated" mid-session. Once an account
 * is known, the user stays authenticated regardless of what MSAL is busy with.
 *
 * The provider also hands out stable identities: `login`, `logout` and
 * `getToken` are `useCallback` and the context value is `useMemo`. A fresh
 * object here re-rendered every consumer on every provider render, and made any
 * consumer effect that depended on `getToken` re-run in a loop.
 *
 * **`getToken` returns null only when the editor must sign in again** — MSAL's
 * `InteractionRequiredAuthError`; the tools then report the expired session,
 * and the banner signs in again in a popup (`reauthenticate`) that keeps the
 * page. Any other failure (a network blip) is tried once more and then thrown,
 * so a tool shows its usual "Bitte noch einmal versuchen" instead of the banner.
 *
 * **Inside the Media Manager's frame** (`embed.ts`) sign-in and sign-out run in
 * a popup: a redirect would have to take the frame to Microsoft's page, which
 * refuses to be framed, and MSAL refuses redirects in a frame anyway.
 */

import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import React, { useCallback, useEffect, useMemo } from 'react'

import { createLoginRequest } from '../config/authConfig'
import { host } from '../embed'
import { AuthContext } from './authContextValue'

/** The wait before the second try of a failed silent token request. */
const TOKEN_RETRY_MS = 1000

const TOKEN_UNAVAILABLE = 'Die Anmeldung konnte gerade nicht bestätigt werden. Bitte noch einmal versuchen.'

/** Stand-in identity used when auth is switched off for local development. */
const DEV_USER: AccountInfo = {
    homeAccountId: 'dev-home-id',
    environment: 'dev',
    tenantId: 'dev-tenant',
    username: 'dev@local',
    localAccountId: 'dev-id',
    name: 'Dev User',
    idTokenClaims: {},
    nativeAccountId: 'dev-native-id',
    authorityType: 'MSSTS',
}

export const AuthProvider: React.FC<{
    children: React.ReactNode
    authEnabled: boolean
}> = ({ children, authEnabled }) => {
    const { instance, accounts, inProgress } = useMsal()

    const { isAuthenticated, user, loading } = useMemo(() => {
        if (!authEnabled) {
            return { isAuthenticated: true, user: DEV_USER, loading: false }
        }
        const account = accounts.length > 0 ? accounts[0] : null
        return {
            isAuthenticated: account !== null,
            user: account,
            // Only "loading" while MSAL is still settling AND we do not yet
            // know of an account — so a silent refresh never looks like a
            // sign-out.
            loading: inProgress !== 'none' && account === null,
        }
    }, [authEnabled, accounts, inProgress])

    const login = useCallback(() => {
        if (!authEnabled) return
        if (host) {
            instance.loginPopup(createLoginRequest()).catch((error) => console.error('Sign-in popup failed:', error))
            return
        }
        instance.loginRedirect(createLoginRequest())
    }, [authEnabled, instance])

    const logout = useCallback(() => {
        if (!authEnabled) return
        if (host) {
            instance.logoutPopup().catch((error) => console.error('Sign-out popup failed:', error))
            return
        }
        instance.logoutRedirect()
    }, [authEnabled, instance])

    // A popup, not a redirect: the editor's page and its running jobs stay.
    // Root renders nothing in that popup, so the app's hash routes cannot
    // overwrite the answer before MSAL reads it.
    const reauthenticate = useCallback(async () => {
        if (!authEnabled) return true
        try {
            await instance.acquireTokenPopup({
                scopes: createLoginRequest().scopes,
                ...(user ? { account: user, loginHint: user.username } : {}),
            })
            return true
        } catch (error) {
            console.error('Signing in again failed:', error)
            return false
        }
    }, [authEnabled, instance, user])

    const getToken = useCallback(async () => {
        if (!authEnabled) return 'dev-token'
        if (!isAuthenticated || !user) return null
        for (let attempt = 1; ; attempt++) {
            try {
                const response = await instance.acquireTokenSilent({
                    ...createLoginRequest(),
                    account: user,
                })
                // The ID token is what the backend validates (audience = client ID).
                return response.idToken
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) return null
                console.error('Token acquisition failed:', error)
                if (attempt >= 2) throw new Error(TOKEN_UNAVAILABLE, { cause: error })
                await new Promise((resolve) => setTimeout(resolve, TOKEN_RETRY_MS))
            }
        }
    }, [authEnabled, instance, isAuthenticated, user])

    // Refresh the token every 12 minutes so a long editing session does not end
    // with a failed request. getToken is stable, so this re-arms only when the
    // auth state it closes over actually changes.
    useEffect(() => {
        if (!authEnabled || !isAuthenticated) return

        const refreshInterval = setInterval(
            async () => {
                try {
                    await getToken()
                } catch (error) {
                    console.error('Failed to auto-refresh token', error)
                }
            },
            12 * 60 * 1000,
        )

        return () => clearInterval(refreshInterval)
    }, [authEnabled, isAuthenticated, getToken])

    const value = useMemo(
        () => ({ isAuthenticated, user, login, logout, reauthenticate, getToken, loading, authEnabled, hostSignIn: false }),
        [isAuthenticated, user, login, logout, reauthenticate, getToken, loading, authEnabled],
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
